<script>
import FullCalendar from '@fullcalendar/vue3'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction'
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import { put, get } from '@rails/request.js'
import TimetableMixin from '../mixins/timetable.vue'

export default {
  components: {
    FullCalendar
  },
  mixins: [TimetableMixin],
  props: {
    eventId: String,
    positionsUrl: String,
    requirementsUrl: String,
    shiftsUrl: String,
    shiftVolunteerUrl: String,
    eventStartDate: String,
    eventEndDate: String,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [ interactionPlugin, bootstrap5Plugin, resourceTimelinePlugin ],
        schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
        initialView: 'resourceTimeline',
        themeSystem: 'bootstrap5',
        height: "auto",
        nowIndicator: true,
        locale: 'de',
        slotDuration: '00:15:00',
        eventOrder: "orderFirst,start,-duration,allDay,title",
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        slotLabelFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
        },
        headerToolbar: {
          left:   'title',
          right:  'editShifts,editRequirements toggleSlotDuration prev,next'
        },
        customButtons: {
          toggleSlotDuration: {
            text: this.$i18n.t("timetable.buttons.toggle_slot_duration"),
            click: this.toggleSlotDuration,
            icon: 'arrows-collapse-vertical',
          },
          editRequirements: {
            text: this.$i18n.t("timetable.positions.buttons.edit_requirements.label"),
            click: this.activateEditRequirementsView,
            icon: 'exclamation-diamond',
          },
          editShifts: {
            text: this.$i18n.t("timetable.positions.buttons.edit_shifts.label"),
            click: this.activateEditShiftsView,
            icon: 'calendar2-range',
          },
        },
        validRange: {
          start: this.eventStartDate,
          end: this.eventEndDate
        },
        events: [{
          start: this.eventStartDate,
          end: this.eventEndDate,
          editable: false,
          display: 'inverse-background',
          backgroundColor: 'grey'
        }],
        eventSources: [
          {
            id: "shifts",
            url: this.shiftsUrl,
          },
          {
            id: "requirements",
            url: this.requirementsUrl,
          },
        ],
        eventDrop: this.onDropOrResize,
        eventResize: this.onDropOrResize,
        dragRevertDuration: 0,
        selectable: true,
        select: this.onSelection,
        eventLongPressDelay: 300,
        eventClick: this.onClick,
        datesSet: this.onDateChanged,
        resources: this.positionsUrl,
        resourceGroupField: 'divisionName',
        resourceOrder: ['divisionName', 'title'],
        resourceAreaColumns: [
          {
            field: 'title',
            headerContent: this.$i18n.t("timetable.positions.headers.position"),
          },
        ]
      },
      requirementView: false,
    }
  },
  channels: {
    EventsChannel: {
      received(data) {
        switch (data.event) {
          case "shifts_changed":
            for (const volunteerId of data.volunteer_ids) {
              this.refreshShiftsOfVolunteer(volunteerId)
            }
            break;
          case "shift_deleted":
            this.getEventByIdAndSource(data.shift_id, 'shifts')?.remove()
            break;
          case "requirements_changed":
            for (const positionId of data.position_ids) {
              this.refreshRequirementOfPosition(positionId)
            }
            break;
          case "requirement_deleted":
            this.getEventByIdAndSource(data.requirement_id, 'requirements')?.remove()
            break;
          case "positions_changed":
            this.$refs.calendar.getApi().refetchResources()
            break;
        }
      }
    },
  },
  mounted() {
    if (!this.requirementsUrl) {
      // User do not have access to requirements,
      // thus buttons to switch modes between editing shifts or requirements are not needed to be shown
      document.querySelector(".btn-group:has(.fc-editShifts-button)").classList.add("d-none")
    } else {
      document.querySelector(".fc-editShifts-button").classList.add("active")
    }

    // Fullcalendar does not let buttons to have icons _and_ text. Given text is automatically set as labels.
    document.querySelector(".fc-editShifts-button > span").appendChild(
      this.createButtonText(this.$i18n.t("timetable.positions.buttons.edit_shifts.text"))
    );
    document.querySelector(".fc-editRequirements-button > span").appendChild(
      this.createButtonText(this.$i18n.t("timetable.positions.buttons.edit_requirements.text"))
    );

    this.$cable.subscribe({
      channel: "EventsChannel",
      eventId: this.eventId,
    });
  },
  methods: {
    activateEditRequirementsView: async function() {
      this.requirementView = true
      document.querySelector(".fc-editRequirements-button").classList.add("active")
      document.querySelector(".fc-editShifts-button").classList.remove("active")
      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateEditShiftsView: async function() {
      this.requirementView = false
      document.querySelector(".fc-editShifts-button").classList.add("active")
      document.querySelector(".fc-editRequirements-button").classList.remove("active")
      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    onDropOrResize: async function(eventResizeOrDropInfo) {
      var shift_or_requirement = eventResizeOrDropInfo.event;

      if (typeof shift_or_requirement.id == 'undefined') {
        eventResizeOrDropInfo.revert();
        return;
      }

      if (shift_or_requirement.source.id === 'shifts') {
        return await this.onDropOrResizeShift(eventResizeOrDropInfo)
      }

      return await this.onDropOrResizeRequirement(eventResizeOrDropInfo)

    },
    onDropOrResizeShift: async function(eventResizeOrDropInfo) {
      const shift = eventResizeOrDropInfo.event

      const response = await put(shift.extendedProps.shiftUrl, { body: JSON.stringify({
        shift: {
          starts_at: shift.start.toISOString(),
          ends_at: shift.end.toISOString(),
          position_id: shift.getResources()[0].id
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onDropOrResizeRequirement: async function(eventResizeOrDropInfo) {
      const requirement = eventResizeOrDropInfo.event

      const response = await put(requirement.extendedProps.requirementUrl, { body: JSON.stringify({
        requirement: {
          starts_at: requirement.start.toISOString(),
          ends_at: requirement.end.toISOString(),
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onSelection: async function(selectionInfo) {
      if (selectionInfo.end - selectionInfo.start < 1800000) {
        // Check against a minimal duration of 30 minutes to skip random clicks directly on the calendar (e.g. to close modal dialogs)
        return
      }

      if (this.requirementView) {
        return await this.onSelectionRequirement(selectionInfo)
      }

      return await this.onSelectionShift(selectionInfo)
    },
    onSelectionShift: async function(selectionInfo) {
      const response = await get(this.shiftVolunteerUrl, { query: {
        "shift[starts_at]": selectionInfo.start.toISOString(),
        "shift[ends_at]": selectionInfo.end.toISOString(),
        "shift[position_id]": selectionInfo.resource.id,
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      $("#app-modal-xl .modal-content").html(content)

      bootstrap.Modal.getOrCreateInstance("#app-modal-xl").show()
    },
    onSelectionRequirement: async function(selectionInfo) {


      const response = await get(selectionInfo.resource.extendedProps.requirementCreateUrl, { query: {
        "requirement[starts_at]": selectionInfo.start.toISOString(),
        "requirement[ends_at]": selectionInfo.end.toISOString(),
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      $("#app-modal-sm .modal-content").html(content)

      bootstrap.Modal.getOrCreateInstance("#app-modal-sm").show()


    },
    refreshRequirementOfPosition: async function(positionId) {
      const calendarApi = this.$refs.calendar.getApi()
      const position = calendarApi.getResourceById(positionId)

      if (!position) {
        return
      }

      const response = await get(this.requirementsUrl, { query: {
        "start": calendarApi.view.currentStart.toISOString(),
        "end": calendarApi.view.currentEnd.toISOString(),
        "position_id": positionId,
      }})
      if (!response.ok) {
        return
      }

      const requirementEventSource = calendarApi.getEventSourceById("requirements")
      const content = await response.json
      for (const requirement of content) {
        this.getEventByIdAndSource(requirement.id, 'requirements')?.remove()
        calendarApi.addEvent(requirement, requirementEventSource)
      }
    },
  }
}
</script>
<template>
  <FullCalendar :options="calendarOptions" ref="calendar">
    <template v-slot:resourceGroupLabelContent='arg'>
      {{ arg.groupValue }}
    </template>

    <template v-slot:resourceLabelContent='arg'>
      <span class="bi-icon bi-circle-fill me-1" :style="{color: arg.resource.extendedProps.color}"></span>
      <div style="display: table-cell">
        <span>
          <a :href="arg.resource.extendedProps.url">{{ arg.resource.title }}</a>
          <span v-if="arg.resource.extendedProps.comment" class="small"><br />{{ arg.resource.extendedProps.comment }}</span>
        </span>
      </div>
    </template>

    <template v-slot:eventContent='arg'>
      <VueTimetableShift v-if="arg.event.source.id == 'shifts'" :event="arg.event" />
      <VueTimetableRequirement v-else :event="arg.event" />
    </template>
  </FullCalendar>
</template>
